import { viewerReadySchema } from '@platform/schema/viewer';
import { useEffect, useRef, useState } from 'react';

import { env } from '../env';

import { isFromViewer, viewerOrigin } from './bridge';

import type { ViewerMessage } from '@platform/schema/viewer';

/**
 * The iframe of `apps/viewer`, and what it is shown — KISS-15.
 *
 * Posts `message` whenever it changes and again each time the viewer says it is
 * listening, so a frame that reloads is caught up. Callers memoise `message`: a fresh
 * object every render is a post every render. `ready` is whether the viewer has
 * announced itself yet.
 */
export function useViewerFrame(message: ViewerMessage | undefined) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(0);

  useEffect(() => {
    const viewerUrl = env.VITE_VIEWER_URL;
    const onReady = (event: MessageEvent<unknown>) => {
      if (
        isFromViewer(event.origin, viewerUrl) &&
        viewerReadySchema.safeParse(event.data).success
      ) {
        setReady((previous) => previous + 1);
      }
    };

    window.addEventListener('message', onReady);

    return () => {
      window.removeEventListener('message', onReady);
    };
  }, []);

  useEffect(() => {
    const target = viewerOrigin(env.VITE_VIEWER_URL);
    if (message === undefined || target === undefined) return;

    frame.current?.contentWindow?.postMessage(message, target);
  }, [message, ready]);

  return { frame, ready: ready > 0 };
}
